import React, { Component } from 'react';
import moment from 'moment';
import TimeInput, { toMinutes } from '../../../sections/proposal-request/components/TimeInput';
import DateInput from '../../../sections/proposal-request/components/DateInput';

const ROOM_TYPES = ['Meeting', 'Breakout', 'General Session', 'Exhibit Hall', 'Pre-function', 'Ballroom', 'Board Room', 'Meal Room', 'Registration/Hospitality', 'Office', 'Storage', 'Dinner/Reception', 'Coffee Break', 'Other'];

const SETUP_PREFERENCES = [
	'No preference', 'Buffet', 'Cabaret', 'Chevron', 'Classroom', 'Conference',
	'Crescent Rounds', 'Flow', 'Hollow Square', 'Island', 'Peninsula', 'Plated',
	'Reception', 'Rounds', 'Rounds of 6', 'Rounds of 8', 'Rounds of 10',
	'Tabletop exhibit', 'Theater', 'T-Shaped', 'U-Shaped', 'Other'
];

/* Generate a collision-proof id for each agenda item */
const makeItemId = () => `item-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/* Generate a collision-proof group id */
const makeGroupId = () => `group-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const BLANK_ITEM = {
	roomType: 'Meeting',
	roomTypeLabel: '',
	startTime: '8:00 AM',
	endTime: '9:00 AM',
	attendees: '',
	copiesPerDay: 1,
	selectedDates: [],
	require24Hour: false,
	setupPreference: [],
	setupPreferenceOther: '',
	sessionName: '',
	roomSize: '',
	roomSizeUnit: 'sq. ft.',
	notes: ''
};

const formatAgendaDuration = (startTime, endTime) => {
	const startMinutes = toMinutes(startTime);
	const endMinutes = toMinutes(endTime);
	if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes) || endMinutes <= startMinutes) {
		return '—';
	}

	return `${((endMinutes - startMinutes) / 60).toFixed(2)} hrs`;
};

class MeetingSpaceStep extends Component {
	constructor(props) {
		super(props);
		this.state = {
			sortBy: 'date',           // 'date' | 'meetingType'
			editingItemIds: null,     // array of item ids being edited, or null
			showExtraDateInput: false,
			extraDateValue: '',
			currentItem: { ...BLANK_ITEM },
			requiredFields: {
				roomType: false,
				selectedDates: false
			}
		};
	}

	updateCurrentItem = (field, value) => {
		this.setState(prev => ({
			currentItem: { ...prev.currentItem, [field]: value }
		}));
	};

	toggleSetupPreference = (pref) => {
		const { currentItem } = this.state;
		this.updateCurrentItem('setupPreference',currentItem.setupPreference.includes(pref) ? [] : [pref]);
	};

	toggleDate = (date) => {
		const { currentItem, requiredFields } = this.state;
		const selectedDates = [...currentItem.selectedDates];
		const idx = selectedDates.indexOf(date);
		if (idx >= 0) {
			selectedDates.splice(idx, 1);
		} else {
			selectedDates.push(date);
		}
		this.updateCurrentItem('selectedDates', selectedDates);
		if (requiredFields.selectedDates && selectedDates.length > 0) {
			this.setState({ requiredFields: { ...requiredFields, selectedDates: false } });
		}
	};

	/**
	 * Determines the groupId to assign when saving edited items.
	 *
	 *  - If ALL members of the original group are in editingItemIds
	 *    AND the result still spans multiple dates → keep the same groupId.
	 *  - Otherwise (partial edit, or reducing to 1 date) → new groupId
	 *    (or undefined when only 1 date remains).
	 */
	resolveGroupId = (editingItemIds, agendaItems, selectedDatesCount) => {
		const firstItem = agendaItems.find(i => editingItemIds.includes(i.id));
		if (!firstItem) {
			return selectedDatesCount > 1 ? makeGroupId() : undefined;
		}

		const originalGroupId = firstItem.groupId;

		if (!originalGroupId) {
			// Was a standalone item — assign a new group only if now multiple dates
			return selectedDatesCount > 1 ? makeGroupId() : undefined;
		}

		// Check if ALL members of the original group are included in editingItemIds
		const allGroupMembers = agendaItems.filter(i => i.groupId === originalGroupId);
		const allSelected = allGroupMembers.every(i => editingItemIds.includes(i.id));

		if (allSelected) {
			// Full group edit — keep groupId if still multi-date, drop if collapsed to 1
			return selectedDatesCount > 1 ? originalGroupId : undefined;
		}

		// Partial group edit — these items break off into a new group (or standalone)
		return selectedDatesCount > 1 ? makeGroupId() : undefined;
	};

	publishToAgenda = () => {
		const { data, onChange } = this.props;
		const { currentItem, editingItemIds, requiredFields } = this.state;

		const updatedRequiredFields = { ...requiredFields };
		let valid = true;

		if (!currentItem.roomType) {
			updatedRequiredFields.roomType = true;
			valid = false;
		} else {
			updatedRequiredFields.roomType = false;
		}

		if (!currentItem.selectedDates || currentItem.selectedDates.length === 0) {
			updatedRequiredFields.selectedDates = true;
			valid = false;
		} else {
			updatedRequiredFields.selectedDates = false;
		}

		if (!valid) {
			this.setState({ requiredFields: updatedRequiredFields });
			return;
		}

		const agendaItems = [...(data.agendaItems || [])];
		// Strip selectedDates from itemFields so we can set it per-date below
		const { selectedDates, ...itemFields } = currentItem;

		// How many agenda entries to create per selected date
		const copies = Math.max(1, parseInt(itemFields.copiesPerDay) || 1);

		if (editingItemIds && editingItemIds.length > 0) {
			// ── Edit mode ────────────────────────────────────────────────────
			const totalItems = selectedDates.length * copies;
			const resolvedGroupId = totalItems > 1
				? this.resolveGroupId(editingItemIds, agendaItems, totalItems)
				: undefined;

			// Identify the original groupId of the items being edited (for orphan cleanup)
			const firstOldItem = agendaItems.find(i => editingItemIds.includes(i.id));
			const originalGroupId = firstOldItem?.groupId;

			// Remove the items being edited
			let filtered = agendaItems.filter(i => !editingItemIds.includes(i.id));

			// If removing those items leaves only ONE item in the original group, that
			// lone item is no longer a group — clear its groupId
			if (originalGroupId) {
				const remainingGroupMembers = filtered.filter(i => i.groupId === originalGroupId);
				if (remainingGroupMembers.length === 1) {
					filtered = filtered.map(i =>
						i.groupId === originalGroupId ? { ...i, groupId: undefined } : i
					);
				}
			}

			// Insert `copies` items per selected date (with the resolved groupId)
			selectedDates.forEach(date => {
				for (let c = 0; c < copies; c++) {
					filtered.push({
						...itemFields,
						id: makeItemId(),
						groupId: resolvedGroupId,
						selectedDates: [date]
					});
				}
			});

			onChange('agendaItems', filtered);
		} else {
			// ── Create mode ──────────────────────────────────────────────────
			const totalItems = selectedDates.length * copies;
			const newGroupId = totalItems > 1 ? makeGroupId() : undefined;

			selectedDates.forEach(date => {
				for (let c = 0; c < copies; c++) {
					agendaItems.push({
						...itemFields,
						id: makeItemId(),
						groupId: newGroupId,
						selectedDates: [date]
					});
				}
			});

			onChange('agendaItems', agendaItems);
		}

		onChange('checkedAgendaItems', {});
		this.setState({ currentItem: { ...BLANK_ITEM }, editingItemIds: null, requiredFields: { roomType: false, selectedDates: false } });
	};

	duplicateCheckedItems = () => {
		const { data, onChange } = this.props;
		const checked = data.checkedAgendaItems || {};
		const agendaItems = [...(data.agendaItems || [])];
		const toDuplicate = agendaItems.filter(item => checked[item.id]);
		if (toDuplicate.length === 0) return;

		// Re-map original groupIds → new groupIds so duplicated groups stay linked
		const groupIdMap = {};
		toDuplicate.forEach(item => {
			if (item.groupId && !groupIdMap[item.groupId]) {
				groupIdMap[item.groupId] = makeGroupId();
			}
		});

		toDuplicate.forEach(item => {
			const newGroupId = item.groupId ? groupIdMap[item.groupId] : undefined;
			agendaItems.push({ ...item, id: makeItemId(), groupId: newGroupId });
		});

		onChange('agendaItems', agendaItems);
		onChange('checkedAgendaItems', {});
	};

	editCheckedItem = () => {
		const { data } = this.props;
		const checkedIds = Object.entries(data.checkedAgendaItems || {}).filter(([, isChecked]) => isChecked).map(([id]) => id);
		if (!checkedIds.length) return;

		const checkedLookup = {};
		checkedIds.forEach((id) => {
			checkedLookup[id] = true;
		});

		const checkedItems = (data.agendaItems || []).filter((item) => checkedLookup[item.id]);
		const firstChecked = checkedItems[0];
		if (!firstChecked) return;

		const dateCounts = {};
		for (const item of checkedItems) {
			for (const date of item.selectedDates || []) {
				if (!date) continue;
				dateCounts[date] = (dateCounts[date] || 0) + 1;
			}
		}

		const aggregatedDates = Object.keys(dateCounts);
		const copiesPerDayFromSelection = Math.max(0, ...Object.values(dateCounts));
		const inferredCopiesPerDay = Math.max(1, copiesPerDayFromSelection, Number(firstChecked.copiesPerDay) || 1);

		const { id, selectedDates: removedSelectedDates, groupId: removedGroupId, ...rest } = firstChecked;
		this.setState({
			currentItem: { ...BLANK_ITEM, ...rest, selectedDates: aggregatedDates, copiesPerDay: inferredCopiesPerDay },
			editingItemIds: checkedIds
		});
		// Keep checkedAgendaItems as-is so checked rows stay highlighted while editing
	};

	/**
	 * Group-constrained selection:
	 *  - The first checked item establishes the "active group" (its groupId).
	 *  - Only items sharing that same groupId can subsequently be checked.
	 *  - If the first checked item is standalone (no groupId), no others may be added.
	 */
	toggleAgendaItem = (id) => {
		const { data, onChange } = this.props;
		const agendaItems = data.agendaItems || [];
		const checkedAgendaItems = { ...(data.checkedAgendaItems || {}) };

		// Unchecking is always allowed
		if (checkedAgendaItems[id]) {
			delete checkedAgendaItems[id];
			onChange('checkedAgendaItems', checkedAgendaItems);
			return;
		}

		// Determine the active group from the current selection
		const currentlyCheckedIds = Object.keys(checkedAgendaItems).filter(k => checkedAgendaItems[k]);
		if (currentlyCheckedIds.length > 0) {
			const firstChecked = agendaItems.find(i => checkedAgendaItems[i.id]);
			const activeGroupId = firstChecked?.groupId;
			const thisItem = agendaItems.find(i => i.id === id);

			if (activeGroupId) {
				// Only allow items in the same group
				if (thisItem?.groupId !== activeGroupId) return;
			} else {
				// Standalone item is already selected — no multi-select allowed
				return;
			}
		}

		checkedAgendaItems[id] = true;
		onChange('checkedAgendaItems', checkedAgendaItems);
	};

	deleteCheckedItems = () => {
		const { data, onChange } = this.props;
		const checked = data.checkedAgendaItems || {};
		const agendaItems = (data.agendaItems || []).filter(item => !checked[item.id]);

		// After deletion, any group left with only 1 member is no longer a group
		const groupCounts = {};
		agendaItems.forEach(i => {
			if (i.groupId) groupCounts[i.groupId] = (groupCounts[i.groupId] || 0) + 1;
		});
		const cleaned = agendaItems.map(i =>
			i.groupId && groupCounts[i.groupId] === 1 ? { ...i, groupId: undefined } : i
		);

		onChange('agendaItems', cleaned);
		onChange('checkedAgendaItems', {});
	};

	addExtraDate = () => {
		const { extraDateValue, currentItem } = this.state;
		if (!extraDateValue) return;
		const formatted = moment(extraDateValue).format('MMM D');
		if (!currentItem.selectedDates.includes(formatted)) {
			this.updateCurrentItem('selectedDates', [...currentItem.selectedDates, formatted]);
		}
		this.setState({ extraDateValue: '', showExtraDateInput: false });
	};

	getAvailableDates = () => {
		const { data } = this.props;
		if (data.preferredArrivalDate && data.preferredDepartureDate) {
			const dates = [];
			let current = moment(data.preferredArrivalDate);
			const end = moment(data.preferredDepartureDate);
			while (current <= end) {
				dates.push(current.format('MMM D'));
				current.add(1, 'days');
			}
			return dates;
		}
		return ['Mar 3', 'Mar 4', 'Mar 5', 'Mar 6'];
	};

	groupAgendaByDate = () => {
		const { data } = this.props;
		const agendaItems = data.agendaItems || [];
		const grouped = {};
		agendaItems.forEach(item => {
			const dates = item.selectedDates || [];
			dates.forEach(date => {
				if (!grouped[date]) grouped[date] = [];
				grouped[date].push(item);
			});
		});
		return grouped;
	};

	groupAgendaByMeetingType = () => {
		const { data } = this.props;
		const agendaItems = data.agendaItems || [];
		const grouped = {};
		agendaItems.forEach(item => {
			const key = item.roomType || 'Other';
			if (!grouped[key]) grouped[key] = [];
			grouped[key].push(item);
		});
		return grouped;
	};

	getItemDurationMinutes = (item) => {
		const start = toMinutes(item.startTime);
		const end = toMinutes(item.endTime);
		if (Number.isNaN(start) || Number.isNaN(end)) return Number.MAX_SAFE_INTEGER;
		if (end >= start) return end - start;
		return (24 * 60 - start) + end;
	};

	sortAgendaItems = (items = []) => {
		return [...items].sort((a, b) => {
			const aStart = toMinutes(a.startTime);
			const bStart = toMinutes(b.startTime);
			const safeAStart = Number.isNaN(aStart) ? Number.MAX_SAFE_INTEGER : aStart;
			const safeBStart = Number.isNaN(bStart) ? Number.MAX_SAFE_INTEGER : bStart;

			const startDiff = safeAStart - safeBStart;
			if (startDiff !== 0) return startDiff;

			const durationDiff = this.getItemDurationMinutes(a) - this.getItemDurationMinutes(b);
			if (durationDiff !== 0) return durationDiff;

			const nameA = (a.sessionName || a.roomTypeLabel || a.roomType || '').toLowerCase();
			const nameB = (b.sessionName || b.roomTypeLabel || b.roomType || '').toLowerCase();
			return nameA.localeCompare(nameB);
		});
	};

	getSortedGroupedAgendaEntries = (groupedAgenda, sortBy) => {
		const baseYear = this.props.data?.preferredArrivalDate
			? moment(this.props.data.preferredArrivalDate).year()
			: moment().year();

		return Object.entries(groupedAgenda)
			.map(([groupKey, items]) => ([groupKey, this.sortAgendaItems(items)]))
			.sort(([keyA], [keyB]) => {
				if (sortBy === 'meetingType') {
					return keyA.localeCompare(keyB);
				}

				const parsedA = moment(`${keyA} ${baseYear}`, 'MMM D YYYY', true);
				const parsedB = moment(`${keyB} ${baseYear}`, 'MMM D YYYY', true);

				if (!parsedA.isValid() && !parsedB.isValid()) return keyA.localeCompare(keyB);
				if (!parsedA.isValid()) return 1;
				if (!parsedB.isValid()) return -1;
				return parsedA.valueOf() - parsedB.valueOf();
			});
	};

	render() {
		const { data } = this.props;
		const { currentItem, sortBy, editingItemIds, requiredFields, showExtraDateInput, extraDateValue } = this.state;
		const availableDates = this.getAvailableDates();
		const groupedAgenda = sortBy === 'meetingType'
			? this.groupAgendaByMeetingType()
			: this.groupAgendaByDate();
		const sortedGroupedAgendaEntries = this.getSortedGroupedAgendaEntries(groupedAgenda, sortBy);
		const agendaItems = data.agendaItems || [];
		const checkedAgendaItems = data.checkedAgendaItems || {};
		const checkedCount = Object.keys(checkedAgendaItems).filter(k => checkedAgendaItems[k]).length;

		// Identify the active group (from the first checked item's groupId)
		const firstChecked = agendaItems.find(i => checkedAgendaItems[i.id]);
		const activeGroupId = firstChecked?.groupId;
		const isOther = currentItem.setupPreference.includes('Other');
		return (
			<div className="rfp-step-content">
				<h1 className="rfp-step-title">Meeting Space &amp; Agenda</h1>

				{/* Room Setup Form */}
				<div className="rfp-meeting-form-grid">
					<div className="rfp-field-group">
						<label className="rfp-field-label">Room Type*</label>
						<div className="rfp-select-wrap">
							<select
								className="rfp-select"
								value={currentItem.roomType}
								onChange={(e) => this.updateCurrentItem('roomType', e.target.value)}
							>
								{ROOM_TYPES.map(rt => (
									<option key={rt} value={rt}>{rt}</option>
								))}
							</select>
							<i className="las la-angle-down rfp-select-arrow" />
						</div>
						<div className="rfp-input-underline" />
						{requiredFields.roomType && (
							<p className="rfp-field-hint">Room type is required.</p>
						)}
					</div>

					<div className="rfp-field-group">
						<label className="rfp-field-label">Start Time</label>
						<TimeInput
							value={currentItem.startTime}
							onChange={(newStart) => {
								this.setState(prev => {
									const updates = { startTime: newStart };
									// Auto-advance end time if it would no longer be after the new start
									if (toMinutes(prev.currentItem.endTime) <= toMinutes(newStart)) {
										const newEnd = moment(newStart, 'h:mm A').add(1, 'hour');
										updates.endTime = newEnd.hours() < 24 ? newEnd.format('h:mm A') : '11:30 PM';
									}
									return { currentItem: { ...prev.currentItem, ...updates } };
								});
							}}
						/>
						<div className="rfp-input-underline" />
					</div>

					<div className="rfp-field-group">
						<label className="rfp-field-label">End Time</label>
						<TimeInput
							value={currentItem.endTime}
							min={currentItem.startTime}
							onChange={(newEnd) => this.updateCurrentItem('endTime', newEnd)}
						/>
						<div className="rfp-input-underline" />
					</div>

					<div className="rfp-field-group">
						<label className="rfp-field-label">Attendees / Estimated GTD</label>
						<input
							className="rfp-text-input"
							type="number"
							min="0"
							value={currentItem.attendees}
							onChange={(e) => this.updateCurrentItem('attendees', e.target.value)}
							placeholder="0"
						/>
						<div className="rfp-input-underline" />
					</div>

					<div className="rfp-field-group">
						<label className="rfp-field-label">Copies per Day</label>
						<input
							className="rfp-text-input"
							type="number"
							value={currentItem.copiesPerDay}
							onChange={(e) => this.updateCurrentItem('copiesPerDay', parseInt(e.target.value) || 1)}
							min="1"
						/>
						<div className="rfp-input-underline" />
					</div>
				</div>

				{/* Other room type label */}
				{currentItem.roomType === 'Other' && (
					<div className="rfp-field-group">
						<label className="rfp-field-label">Room Type Label</label>
						<input
							className="rfp-text-input"
							type="text"
							value={currentItem.roomTypeLabel || ''}
							onChange={(e) => this.updateCurrentItem('roomTypeLabel', e.target.value)}
							placeholder="Describe the room type"
						/>
						<div className="rfp-input-underline" />
					</div>
				)}

				{/* Select Dates */}
				<div className="rfp-field-group">
					<label className="rfp-field-label">Select Dates*</label>
					<div className="rfp-date-pills">
						{availableDates.map((date) => {
							const isSelected = currentItem.selectedDates.includes(date);
							const isFirst = currentItem.selectedDates[0] === date;
							const isLast = currentItem.selectedDates[currentItem.selectedDates.length - 1] === date;
							return (
								<button
									key={date}
									className={`rfp-date-pill ${isSelected ? 'active' : ''} ${isFirst && currentItem.selectedDates.length > 1 ? 'first' : ''} ${isLast && currentItem.selectedDates.length > 1 ? 'last' : ''}`}
									onClick={() => this.toggleDate(date)}
								>
									{date}
								</button>
							);
						})}
						{/* Extra dates added outside the arrival/departure range */}
						{currentItem.selectedDates
							.filter(d => !availableDates.includes(d))
							.map(d => (
								<button
									key={d}
									className="rfp-date-pill active"
									onClick={() => this.toggleDate(d)}
								>
									{d}
								</button>
							))
						}
					</div>

					{/* Add additional date */}
					{showExtraDateInput ? (
						<div className="rfp-extra-date-row">
							<div className="rfp-date-input-wrap">
								<DateInput
									value={extraDateValue}
									onChange={(val) => {
										if (val) {
											const formatted = moment(val, 'YYYY-MM-DD').format('MMM D');
											const current = this.state.currentItem.selectedDates;
											if (!current.includes(formatted)) {
												this.updateCurrentItem('selectedDates', [...current, formatted]);
											}
											this.setState({ extraDateValue: '', showExtraDateInput: false });
										}
									}}
									className="rfp-date-input rfp-extra-date-input"
									placeholder="MM/DD/YYYY"
									allowManualInput
								/>
								<i className="las la-calendar rfp-date-icon" />
							</div>
							<button className="rfp-extra-date-cancel" onClick={() => this.setState({ showExtraDateInput: false, extraDateValue: '' })}>
								<i className="las la-times" />
							</button>
						</div>
					) : (
						<button
							className="rfp-date-pill rfp-date-pill-add"
							style={{ marginTop: 8 }}
							onClick={() => this.setState({ showExtraDateInput: true })}
						>
							<i className="las la-plus" /> Add Date
						</button>
					)}

					{requiredFields.selectedDates && (
						<p className="rfp-field-hint">At least one date is required to publish.</p>
					)}
				</div>

				{/* 24-hour hold */}
				<div className="rfp-toggle-row">
					<div
						className={`rfp-toggle-switch ${currentItem.require24Hour ? 'active' : ''}`}
						onClick={() => this.updateCurrentItem('require24Hour', !currentItem.require24Hour)}
					>
						<div className="rfp-toggle-thumb" />
					</div>
					<span className="rfp-toggle-label">Require 24-hour hold</span>
				</div>

				{/* Setup Preference */}
				<div className="rfp-field-group">
					<label className="rfp-field-label">Setup Preference</label>
					<div className="rfp-setup-chips">
						{SETUP_PREFERENCES.map((pref) => (
							<button
								key={pref}
								className={`rfp-setup-chip ${currentItem.setupPreference.includes(pref) ? 'active' : ''}`}
								onClick={() => this.toggleSetupPreference(pref)}
							>
								{pref}
							</button>
						))}
					</div>
				</div>
				{isOther && (
					<div className="rfp-field-group">
						<label className="rfp-field-label">Specify Setup Preference</label>
						<input
							className="rfp-text-input"
							type="text"
							value={currentItem.setupPreferenceOther || ''}
							onChange={(e) => this.updateCurrentItem('setupPreferenceOther', e.target.value)}
							placeholder="Describe the setup preference"
						/>
						<div className="rfp-input-underline" />
					</div>
				)}

				{/* Additional Details */}
				<div className="rfp-additional-details">
					<div className="rfp-add-details-grid">
						<div className="rfp-field-group">
							<label className="rfp-field-label">Session name</label>
							<input
								className="rfp-text-input"
								type="text"
								value={currentItem.sessionName}
								onChange={(e) => this.updateCurrentItem('sessionName', e.target.value)}
								placeholder="Enter session name"
							/>
							<div className="rfp-input-underline" />
						</div>

						<div className="rfp-field-group">
							<label className="rfp-field-label">Room Size</label>
							<div className="rfp-room-size-wrap">
								<input
									className="rfp-text-input"
									type="number"
									value={currentItem.roomSize}
									min="0"
									onChange={(e) => this.updateCurrentItem('roomSize', e.target.value)}
									placeholder="0"
								/>
								<div className="rfp-select-wrap rfp-room-size-unit">
									<select
										className="rfp-select"
										value={currentItem.roomSizeUnit}
										onChange={(e) => this.updateCurrentItem('roomSizeUnit', e.target.value)}
									>
										<option>sq. ft.</option>
										<option>sq. m.</option>
									</select>
									<i className="las la-angle-down rfp-select-arrow" />
								</div>
							</div>
							<div className="rfp-input-underline" />
						</div>
					</div>

					<div className="rfp-field-group">
						<label className="rfp-field-label">Notes</label>
						<textarea
							className="rfp-textarea"
							value={currentItem.notes}
							onChange={(e) => this.updateCurrentItem('notes', e.target.value)}
							placeholder="Add any additional notes for this session"
							rows={3}
						/>
					</div>
				</div>

				{/* Publish to Agenda / Update Item */}
				<div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 40 }}>
					<button
						className="rfp-publish-btn"
						style={{ marginBottom: 0 }}
						onClick={this.publishToAgenda}
					>
						<i className={`las ${editingItemIds ? 'la-check' : 'la-plus-circle'}`} />
						{editingItemIds
							? (editingItemIds.length > 1 ? 'Update Group' : 'Update Item')
							: 'Publish to Agenda'
						}
					</button>
					{editingItemIds && (
						<button
							className="rfp-agenda-batch-btn"
							onClick={() => {
								this.props.onChange('checkedAgendaItems', {});
							this.setState({ currentItem: { ...BLANK_ITEM }, editingItemIds: null, requiredFields: { roomType: false, selectedDates: false } });
							}}
						>
							Cancel Edit
						</button>
					)}
				</div>

				{/* Agenda Section */}
				{Object.keys(groupedAgenda).length > 0 && (
					<div className="rfp-agenda-section">
						<div className="rfp-agenda-header-row">
							<h1 className="rfp-step-title rfp-agenda-title">Agenda</h1>
							<div className="rfp-agenda-sort-btns">
								<button
									className={`rfp-agenda-sort ${sortBy === 'date' ? 'active' : ''}`}
									onClick={() => this.setState({ sortBy: 'date' })}
								>
									Date
								</button>
								<button
									className={`rfp-agenda-sort ${sortBy === 'meetingType' ? 'active' : ''}`}
									onClick={() => this.setState({ sortBy: 'meetingType' })}
								>
									Meeting Type
								</button>
							</div>
						</div>

						{checkedCount > 0 && !editingItemIds && (
							<div className="rfp-agenda-batch-actions">
								<button className="rfp-agenda-batch-x" onClick={() => this.props.onChange('checkedAgendaItems', {})}>
									<i className="las la-times" />
								</button>
								<span>{checkedCount} {checkedCount === 1 ? 'item' : 'items'} selected</span>

								<button className="rfp-agenda-batch-btn" onClick={this.duplicateCheckedItems}>Duplicate</button>
								<button className="rfp-agenda-batch-btn" onClick={this.editCheckedItem}>Edit</button>
								<button className="rfp-agenda-batch-btn rfp-agenda-batch-delete" onClick={this.deleteCheckedItems}>Delete</button>
							</div>
						)}

						{sortedGroupedAgendaEntries.map(([date, items]) => (
							<div key={date} className="rfp-agenda-date-group">
								<div className="rfp-agenda-date-header">{date}</div>
								{items.map((item) => {
									const isChecked = !!checkedAgendaItems[item.id];

									// All checkboxes are locked while in edit mode
									let isDisabled = !!editingItemIds;

									// Outside edit mode: apply group constraints
									if (!isDisabled && checkedCount > 0 && !isChecked) {
										if (activeGroupId) {
											isDisabled = item.groupId !== activeGroupId;
										} else {
											isDisabled = true;
										}
									}

									return (
										<div
											key={item.id}
											className={[
												'rfp-agenda-row',
												isChecked  ? 'checked'       : '',
												isDisabled ? 'check-disabled' : ''
											].filter(Boolean).join(' ')}
										>
											<input
												type="checkbox"
												className="rfp-agenda-checkbox"
												checked={isChecked}
												disabled={isDisabled}
												onChange={() => this.toggleAgendaItem(item.id)}
											/>

											<div className="rfp-agenda-time">
												<div>{item.startTime} – {item.endTime}</div>
												<div className="rfp-agenda-duration">{formatAgendaDuration(item.startTime, item.endTime)}</div>
											</div>
											<div className="rfp-agenda-session-name">{item.sessionName || ''}</div>
											<div className="rfp-agenda-room-type">
												{item.roomType === 'Other' && item.roomTypeLabel ? item.roomTypeLabel : item.roomType}
											</div>
											{item.require24Hour && <i className="las la-redo-alt rfp-agenda-icon" title="24-hr hold" />}
											{item.attendees && (
												<div className="rfp-agenda-attendees">
													<i className="las la-user" /> {item.attendees}
												</div>
											)}
											{item.roomSize && (
												<div className="rfp-agenda-room-size">
													<i className="las la-expand" /> {item.roomSize} {item.roomSizeUnit || 'sq. ft.'}
												</div>
											)}
											{item.setupPreference && item.setupPreference.length > 0 && (
												<div className="rfp-agenda-setup">
													<i className="las la-th" /> {item.setupPreference[0] === 'Other' ? item.setupPreferenceOther : item.setupPreference[0]}
												</div>
											)}
											{item.notes && (
												<div className="rfp-agenda-notes" title={item.notes}>
													<i className="las la-sticky-note" />
													<span className="rfp-agenda-notes-text">{item.notes}</span>
												</div>
											)}
										</div>
									);
								})}
							</div>
						))}
					</div>
				)}
			</div>
		);
	}
}

export default MeetingSpaceStep;
